import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import PhSidebarSimple from 'ember-phosphor-icons/components/ph-sidebar-simple';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import Modal from 'tio-ui/components/modal';
import TooltipButton from 'tio-ui/components/tooltip-button';
import Footer from 'tio-ui/components/footer';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import { VStack } from 'tio-ui/components/layout';
import AlertBanner from 'tio-ui/components/alert-banner';
import { not, or } from 'tio-ui/utilities';
import { dropTask } from 'ember-concurrency';
import { divide } from 'tio-ui/math';
import inputmask from '../../../modifiers/inputmask.ts';
import { getInputmaskUnmaskedValue } from '../../../utils/inputmask.ts';
import safeFormatNumber from '../../../helpers/safe-format-number';
import LimitsSummary from '../../tuition-assistance/limits-summary.gts';
import { limitsEvaluationDate } from '../../../utils/tuition-assistance/limits-evaluation-date.ts';
import SelectTextArea from '../../tio/select-text-area.gts';
import TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklist from '../../tuition-assistance/application-details/courses-approval-waiver-checklist.gts';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import ErrorMessages from 'tio-ui/components/error-messages';
import {
  buildTemplateResponseOptions,
  getSingleValueForTasField,
  setTrackedTasFieldValue,
} from '../../../utils/tuition-assistance/fields.ts';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';
import CalculatePrePercentageValue from '../../../utils/tuition-assistance/calculate-pre-percentage-value.ts';
import type Owner from '@ember/owner';
import type { Store } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type TuitionAssistanceService from '../../../services/tuition-assistance';
import type ApprovalQueueRefreshService from '../../../services/approval-queue-refresh';
import type FeaturesService from '../../../services/features';
import type { NotificationsService } from 'tio-ui/components/notifications';

import type SessionContextService from '../../../services/session-context.ts';
import { findRecord } from '@warp-drive/utilities/json-api';
import { saveRecord } from 'tio-data/requests/save-record';
import { reportSeveredLink } from 'tio-data/utils/severed-link-report';
import {
  waiveEvidenceApprovalRequest,
  updateApprovedTotalRequest,
  approveCoursesRequest,
  approveEvidenceRequest,
  type TasApplicationTransitionFieldData,
} from 'tio-data/requests/tas-application-actions';
import { buildTasActorFromSession } from 'tio-data/utils/tas-actor';
/**
 * Approves TAS courses or evidence, including amount validation, Course Cash totals, and evidence
 * waivers.
 */
interface ApproveModalSignature {
  Element: HTMLDivElement;
  Args: {
    /** Controls visibility of the approval form; closing waits for the modal exit transition. */
    show: boolean;
    /** Leaves the page and its reference drawers usable while the form is open. */
    nonModal?: boolean;
    /** Requests dismissal from the caller; suppressed while an approval submission is processing. */
    onClose: () => void;
    /** Supplies approval state and amounts; submission updates totals, transitions, and reloads this record. */
    application: TasApplicationModel;
    /** Persisted comment draft supplied by the footer */
    draftComment: string;
    /** Propagate comment draft changes back to the footer */
    onDraftCommentChange: (value: string) => void;
  };
}

class ApproveModal extends Component<ApproveModalSignature> {
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare approvalQueueRefresh: ApprovalQueueRefreshService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  @tracked approvedTotal = 0;
  @tracked cardFinalApprovedTotal = 0;
  @tracked comment = '';
  @tracked submissionError: string | null = null;

  // Reset the instructions panel closed whenever the modal opens.
  @tracked isInstructionsOpen = false;

  // Pre-percentage values (for percentage-based calculations)
  @tracked prePercentageTotal = 0;
  @tracked prePercentageCourseCashTotal = 0;

  // Seed currency input strings on open only; rewriting during typing remounts inputmask and steals focus.
  @tracked approvedTotalInputValue = '0';
  @tracked cardFinalApprovedTotalInputValue = '0';
  @tracked prePercentageTotalInputValue = '0';
  @tracked prePercentageCourseCashTotalInputValue = '0';

  @tracked hasConfirmedEvidenceAttachmentWaiver = false;
  @tracked hasConfirmedEvidenceApprovalWaiver = false;
  @tracked hasRespondedToAllChecklistItems = false;

  @tracked isProcessing = false;
  @tracked showConfirmApproverOverridePrompt = false;

  constructor(owner: Owner, args: ApproveModalSignature['Args']) {
    super(owner, args);
    if (args.show) {
      this.initializeFormDefaults();
    }
  }

  /**
   * Admin-authored header HTML, or null when unset. Render only in the sandboxed iframe to isolate
   * styles and scripts.
   */
  get headerHtml() {
    const fields = this.args.application.tasProgramInstance?.tasProgramTemplate?.fields;
    if (!fields) {
      return null;
    }
    const value = getSingleValueForTasField('APPROVAL_MODAL_HEADER_HTML', fields);
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  }

  get modalSize() {
    return this.headerHtml ? '4xl' : 'xl';
  }

  /** Ordinal-sorted program instructions remain accessible alongside the approval form. */
  get approverInstructions() {
    const instructions =
      this.args.application.tasProgramInstance?.tasProgramTemplate?.programApproverInstructions ??
      [];
    const instructionWord = this.intl.t('tuition_assistance.application_details.instruction');
    return instructions.map((instruction) => ({
      heading: instruction.heading || `${instructionWord} ${instruction.ordinal}`,
      body: instruction.body,
      ordinal: instruction.ordinal,
    }));
  }

  get hasApproverInstructions() {
    return this.approverInstructions.length > 0;
  }

  toggleInstructions = () => {
    this.isInstructionsOpen = !this.isInstructionsOpen;
  };

  /** Reset form state from current application values whenever the modal opens. */
  initializeFormDefaults() {
    // TODO: Auto-populate approved total based on calculation strategy
    // For now, initialize with requested amount or existing approved amount
    const { application } = this.args;
    const reimbursementPercentage = this.reimbursementPercentage;

    if (this.isCourseCash) {
      const cardTotal = application.cardFinalApprovedTotal ?? application.cardPreApprovedTotal ?? 0;
      this.cardFinalApprovedTotal = typeof cardTotal === 'number' ? cardTotal : 0;

      // Course Cash tuition is separate; other expenses default to zero.
      const existingApprovedTotal = application.approvedTotal || 0;
      this.approvedTotal = existingApprovedTotal > 0 ? existingApprovedTotal : 0;

      if (
        this.isCalculatedApplicationTotal &&
        reimbursementPercentage &&
        reimbursementPercentage > 0
      ) {
        this.prePercentageCourseCashTotal = CalculatePrePercentageValue(
          this.cardFinalApprovedTotal,
          reimbursementPercentage
        );
        this.prePercentageTotal = CalculatePrePercentageValue(
          this.approvedTotal,
          reimbursementPercentage
        );
      }
    } else {
      const existingApprovedTotal = application.approvedTotal || 0;
      this.approvedTotal =
        existingApprovedTotal > 0 ? existingApprovedTotal : application.requestedTotal || 0;

      if (this.isCalculatedApplicationTotal && reimbursementPercentage) {
        this.prePercentageTotal = CalculatePrePercentageValue(
          this.approvedTotal,
          reimbursementPercentage
        );
      }
    }

    // Seed dollar display strings once; avoid rewriting controlled values during typing.
    this.approvedTotalInputValue = String(this.approvedTotal / 100);
    this.cardFinalApprovedTotalInputValue = String(this.cardFinalApprovedTotal / 100);
    this.prePercentageTotalInputValue = String(this.prePercentageTotal / 100);
    this.prePercentageCourseCashTotalInputValue = String(this.prePercentageCourseCashTotal / 100);

    this.comment = this.args.draftComment || this.templateApproveComment;
    this.submissionError = null;
    this.hasConfirmedEvidenceAttachmentWaiver = false;
    this.hasConfirmedEvidenceApprovalWaiver = false;
  }

  resetFormState() {
    this.approvedTotal = 0;
    this.cardFinalApprovedTotal = 0;
    this.prePercentageTotal = 0;
    this.prePercentageCourseCashTotal = 0;
    this.approvedTotalInputValue = '0';
    this.cardFinalApprovedTotalInputValue = '0';
    this.prePercentageTotalInputValue = '0';
    this.prePercentageCourseCashTotalInputValue = '0';
    this.comment = this.args.draftComment;
    this.submissionError = null;
    this.hasConfirmedEvidenceAttachmentWaiver = false;
    this.hasConfirmedEvidenceApprovalWaiver = false;
    this.hasRespondedToAllChecklistItems = false;
    this.showConfirmApproverOverridePrompt = false;
    this.isProcessing = false;
  }

  get isPendingCoursesApproval() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL';
  }

  get isPendingEvidenceApproval() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL';
  }

  /**
   * Prepaid evidence review already includes this payment in annual usage, so limits validation
   * would double-count it.
   */
  get isPostPaymentReview() {
    return this.isPendingEvidenceApproval && (this.args.application.amounts?.paid ?? 0) > 0;
  }

  get isCourseCash() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get isVoucherProgram() {
    return !!this.args.application.tasProgramInstance?.tasProgramTemplate?.isVoucherProgram;
  }

  get isCreditBased() {
    return this.args.application.isCreditBased;
  }

  get isCalculatedApplicationTotal() {
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    if (!template) return false;
    return template.calculateTotalByPercent || template.calculateTotalByGPA;
  }

  get reimbursementPercentage() {
    return (
      this.args.application.tasProgramInstance?.tasProgramTemplate?.reimbursementPercentage ?? 0
    );
  }

  /**
   * Per-course expenses remain visible while the modal blocks the underlying page. courseTotal
   * matches requestedTotalAllCourses for credit-rate programs.
   */
  get requestedExpenseRows() {
    // Course Cash has separate tuition and reimbursement inputs; a combined total would describe neither.
    if (this.isCourseCash) {
      return [];
    }

    const rows = this.args.application.activeCourses.map((course, index) => {
      const number = String(course.courseNumber ?? '').trim();
      const name = String(course.courseName ?? '').trim();

      return {
        label:
          [number, name].filter(Boolean).join(' — ') ||
          this.intl.t('common.tas.approval.approve_modal.requested_expenses.course_fallback', {
            number: index + 1,
          }),
        amount: course.courseTotal,
      };
    });

    const { scholarshipsTotal } = this.args.application;

    // Scholarships deduct from course rows; alone they would show a misleading zero-total breakdown.
    if (rows.length && scholarshipsTotal > 0) {
      rows.push({
        label: this.intl.t('common.tas.approval.approve_modal.requested_expenses.scholarships'),
        amount: -scholarshipsTotal,
      });
    }

    return rows;
  }

  /** Total before reimbursement percentage, suitable for the percentage calculation input. */
  get requestedExpensesTotal() {
    const { requestedTotalAllCourses, scholarshipsTotal } = this.args.application;
    return Math.max(0, requestedTotalAllCourses - scholarshipsTotal);
  }

  get responseTemplateFieldName() {
    if (this.isPendingCoursesApproval) {
      return 'COURSES_APPROVAL_APPROVE_TEXT';
    }

    if (this.isPendingEvidenceApproval) {
      return 'EVIDENCE_APPROVAL_APPROVE_TEXT';
    }

    return null;
  }

  private getTagForApproval(): string | null {
    if (this.isPendingCoursesApproval) return 'TAS.CourseApproval.APPROVE';
    if (this.isPendingEvidenceApproval) return 'TAS.EvidenceApproval.APPROVE';
    return null;
  }

  private companyCommentsForTag(tag: string): { title: string; body: string }[] {
    const partnerComments =
      this.args.application.tasProgramInstance?.employee?.company?.comments ?? [];
    return partnerComments
      .filter((comment) => comment.tags?.includes(tag))
      .map((comment) => comment.tasApprovalNote);
  }

  // Prefill and dropdown share merged program and company responses so neither source hides the other.
  get combinedApproveOptions(): { title: string; body: string }[] {
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    const templateOptions = buildTemplateResponseOptions(
      this.responseTemplateFieldName,
      template?.fields,
      template?.tasFieldOptions ?? [],
      this.intl.t('common.tas.approval.program_response_label')
    );
    const tag = this.getTagForApproval();
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];

    return [...templateOptions, ...companyOptions];
  }

  get templateApproveComment() {
    const allOptions = this.combinedApproveOptions;
    return allOptions.length === 1 ? (allOptions[0]?.body ?? '') : '';
  }

  /** Multiple approve responses render a dropdown; a lone response prefills the plain textarea. */
  get approveTextAreaOptions() {
    const allOptions = this.combinedApproveOptions;
    return allOptions.length > 1 ? allOptions : [];
  }

  get fieldToUpdate(): 'PRE_APPROVED_TOTAL' | 'FINAL_APPROVED_TOTAL' {
    const beforePreApprovalStates = [
      'TAS.ApplicationState.CREATED',
      'TAS.ApplicationState.DEFINE_COURSES',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ];

    return beforePreApprovalStates.includes(this.args.application.state)
      ? 'PRE_APPROVED_TOTAL'
      : 'FINAL_APPROVED_TOTAL';
  }

  get showCourseCashField() {
    return this.isCourseCash && this.isPendingCoursesApproval;
  }

  get canSkipFinalApprovalOrAttachmentsWithChecklist() {
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    if (!template) {
      reportSeveredLink({
        observer: 'approve-modal',
        edge: this.args.application.tasProgramInstance
          ? 'tasProgramInstance.tasProgramTemplate'
          : 'tasApplication.tasProgramInstance',
        recordId: this.args.application.id ?? undefined,
      });
      // The waiver flag is unreadable, so degrade to the standard no-skip approval flow.
      return false;
    }
    return (
      this.isPendingCoursesApproval &&
      getSingleValueForTasField('ALLOW_WAIVER_OF_EVIDENCE_APPROVAL', template.fields) === true
    );
  }

  get isExpectedApprover() {
    const expectedApproverEmployeeIds = this.args.application.currentExpectedApproverEmployeeIds;
    const employees = this.sessionContext.user?.person?.employees || [];
    return doEmployeesExistInApproverList(employees, expectedApproverEmployeeIds);
  }

  get employeeHasAppsInRepayment() {
    return !!this.appsInRepayment.length;
  }

  get appsInRepayment() {
    // This would need to be fetched from the employee's applications
    // For now, return empty array - implementation depends on data availability
    return [];
  }

  /**
   * Voucher reimbursement may be zero because VOUCHER_AMOUNT is separate. Course Cash course
   * approval needs card funds; evidence approval accepts reimbursement, card totals, or ledger
   * funds. Other programs require reimbursement funds; negative totals are invalid.
   */
  get hasValidTotal() {
    if (this.isVoucherProgram) {
      return this.approvedTotal >= 0;
    }
    if (this.showCourseCashField) {
      return this.cardFinalApprovedTotal > 0;
    }
    if (this.isCourseCash) {
      return this.approvedTotal > 0 || this.cardFinalApprovedTotal > 0 || this.hasCourseCashValue;
    }
    return this.approvedTotal > 0;
  }

  /**
   * Auto-approval may fund cards without setting CARD_* fields. Refunds move assistance out of used
   * states, so refunded value independently proves prior Course Cash funding.
   */
  get hasCourseCashValue() {
    const courseCash = this.args.application.amounts?.course_cash;
    if (!courseCash) {
      return false;
    }
    return (
      (courseCash.reserved ?? 0) > 0 || (courseCash.used ?? 0) > 0 || (courseCash.refunded ?? 0) > 0
    );
  }

  /**
   * User-facing blockers exclude the processing guard so they remain accurate during submission.
   * The template adds LimitsSummary validation from its block scope.
   */
  get validationBlockersForDisplay(): string[] {
    const blockers: string[] = [];

    if (!this.isPostPaymentReview) {
      if (!this.hasValidTotal) {
        blockers.push(
          this.intl.t('common.tas.approval.approve_modal.validation_blockers.amount_required')
        );
      }
    }

    if (!this.comment) {
      blockers.push(
        this.intl.t('common.tas.approval.approve_modal.validation_blockers.comment_required')
      );
    }

    if (
      this.canSkipFinalApprovalOrAttachmentsWithChecklist &&
      !this.hasRespondedToAllChecklistItems
    ) {
      blockers.push(
        this.intl.t(
          'common.tas.approval.approve_modal.validation_blockers.waiver_checklist_required'
        )
      );
    }

    return blockers;
  }

  get hasValidationBlockers() {
    return this.validationBlockersForDisplay.length > 0;
  }

  get approvalQueueRoute() {
    if (this.features.isEmployeeApp) {
      return 'authenticated.admin.tas.approval-queue';
    }
    return 'authenticated.tas.approval-queue';
  }

  get isTasParticipantInactive() {
    const programInstance = this.args.application.tasProgramInstance;
    if (!programInstance) {
      reportSeveredLink({
        observer: 'approve-modal',
        edge: 'tasApplication.tasProgramInstance',
        recordId: this.args.application.id ?? undefined,
      });
      // Activity is unreadable; never assert "inactive" from a severed link.
      return false;
    }
    return !programInstance.employee?.tasParticipant?.isActive;
  }

  get canSubmitApproval() {
    if (this.isPostPaymentReview) {
      return !this.isProcessing && this.comment.length > 0;
    }

    if (this.canSkipFinalApprovalOrAttachmentsWithChecklist) {
      return (
        !this.isProcessing &&
        this.hasValidTotal &&
        this.comment.length > 0 &&
        this.hasRespondedToAllChecklistItems
      );
    }

    return !this.isProcessing && this.hasValidTotal && this.comment.length > 0;
  }

  /** Read unmasked dollars from inputmask before updating the approved total in cents. */
  updateApprovedTotal = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event) ?? '0';
    this.approvedTotal = Math.round(Number(unmaskedValue) * 100);
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  updateCardFinalApprovedTotal = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event) ?? '0';
    this.cardFinalApprovedTotal = Math.round(Number(unmaskedValue) * 100);
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  updateApprovedTotalFromPrePercentage = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event) ?? '0';
    const numericValue = Number(unmaskedValue);
    const reimbursementPercentage = this.reimbursementPercentage;

    if (isNaN(numericValue) || !reimbursementPercentage || isNaN(reimbursementPercentage)) {
      this.approvedTotal = 0;
      this.prePercentageTotal = 0;
      return;
    }

    const updatedPrePercentageTotal = Math.round(numericValue * 100);
    this.prePercentageTotal = updatedPrePercentageTotal;
    this.approvedTotal = Math.round(updatedPrePercentageTotal * (reimbursementPercentage / 100));
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  updateCourseCashFromPrePercentage = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event) ?? '0';
    const numericValue = Number(unmaskedValue);
    const reimbursementPercentage = this.reimbursementPercentage;

    if (isNaN(numericValue) || !reimbursementPercentage || isNaN(reimbursementPercentage)) {
      this.cardFinalApprovedTotal = 0;
      this.prePercentageCourseCashTotal = 0;
      return;
    }

    const updatedPrePercentageTotal = Math.round(numericValue * 100);
    this.prePercentageCourseCashTotal = updatedPrePercentageTotal;
    this.cardFinalApprovedTotal = Math.round(
      updatedPrePercentageTotal * (reimbursementPercentage / 100)
    );
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  updateComment = (value: string) => {
    this.comment = value;
    this.args.onDraftCommentChange(value);
  };

  didChangeApprovalWaiverChecklist = ({
    hasConfirmedEvidenceApprovalWaiver,
    hasConfirmedEvidenceAttachmentWaiver,
    hasRespondedToAllChecklistItems,
  }: {
    hasConfirmedEvidenceApprovalWaiver: boolean;
    hasConfirmedEvidenceAttachmentWaiver: boolean;
    hasRespondedToAllChecklistItems: boolean;
  }) => {
    this.hasConfirmedEvidenceApprovalWaiver = hasConfirmedEvidenceApprovalWaiver;
    this.hasConfirmedEvidenceAttachmentWaiver = hasConfirmedEvidenceAttachmentWaiver;
    this.hasRespondedToAllChecklistItems = hasRespondedToAllChecklistItems;
  };

  handleClose = () => {
    if (!this.isProcessing) {
      this.args.onClose();
    }
  };

  /**
   * Applies selected evidence waivers and persists the application; full approval waiver also
   * waives attachments.
   */
  waiveRequirementsIfApplicable = async (application: TasApplicationModel) => {
    if (
      !this.canSkipFinalApprovalOrAttachmentsWithChecklist ||
      // Evidence approval can only be waived if attachment requirement is waived also
      !this.hasConfirmedEvidenceAttachmentWaiver
    ) {
      return;
    }

    if (this.hasConfirmedEvidenceApprovalWaiver) {
      // The endpoint sets both waiver flags and overrides approval summaries to skip subsequent steps.
      return this.store.request(waiveEvidenceApprovalRequest(application.id!));
    } else {
      setTrackedTasFieldValue(application, 'WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT', true);
      return saveRecord(this.store, application);
    }
  };

  attemptSubmitApproval = () => {
    if (!this.isExpectedApprover) {
      this.showConfirmApproverOverridePrompt = true;
    } else {
      this.submitApprovalTask.perform();
    }
  };

  cancelApproverOverride = () => {
    this.showConfirmApproverOverridePrompt = false;
  };

  submitApprovalTask = dropTask(async () => {
    this.showConfirmApproverOverridePrompt = false;
    this.isProcessing = true;
    this.submissionError = null;

    try {
      const { application } = this.args;

      if (!this.isPostPaymentReview) {
        const additionalFields: Record<string, unknown> = {};

        if (this.showCourseCashField) {
          additionalFields['CARD_FINAL_APPROVED_TOTAL'] = this.cardFinalApprovedTotal;
        }

        await this.store.request(
          updateApprovedTotalRequest(
            application.id!,
            this.approvedTotal,
            this.comment,
            additionalFields
          )
        );
        const { content: reloadedContent } = (await this.store.request(
          findRecord<TasApplicationModel>('tas-application', application.id!, {
            include: 'tas-assistances',
            reload: true,
          })
        )) as { content: { data: TasApplicationModel } };
        void reloadedContent; // result unused — reload updates the store cache in place
      }

      if (this.isVoucherProgram) {
        const employee = application.tasProgramInstance?.employee;
        // Fetch limits at submission because the visible summary may still be loading or stale.
        const limitsData = employee
          ? await this.tuitionAssistance.getV2LimitsStatus(
              employee,
              application,
              limitsEvaluationDate(application)
            )
          : this.tuitionAssistance.limitsData;
        const voucherAmount = limitsData?.available ?? 0;
        setTrackedTasFieldValue(application, 'VOUCHER_AMOUNT', voucherAmount);
        await saveRecord(this.store, application);
      }

      if (this.hasConfirmedEvidenceAttachmentWaiver) {
        await this.waiveRequirementsIfApplicable(application);
      }

      const actor = buildTasActorFromSession(this.sessionContext);
      const fieldData: TasApplicationTransitionFieldData = {
        preApprovedTotal:
          (getSingleValueForTasField('PRE_APPROVED_TOTAL', application.fields) as number | null) ??
          null,
        finalApprovedTotal:
          (getSingleValueForTasField('FINAL_APPROVED_TOTAL', application.fields) as
            number | null) ?? null,
        waiveEvidenceAttachmentRequirement:
          (getSingleValueForTasField(
            'WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT',
            application.fields
          ) as boolean | null) ?? false,
        waiveEvidenceApprovalRequirement:
          (getSingleValueForTasField('WAIVE_EVIDENCE_APPROVAL_REQUIREMENT', application.fields) as
            boolean | null) ?? false,
      };

      if (this.isPendingCoursesApproval) {
        await this.store.request(
          approveCoursesRequest(application.id!, actor, this.comment, fieldData)
        );
      } else {
        await this.store.request(
          approveEvidenceRequest(application.id!, actor, this.comment, fieldData)
        );
      }
      const { content: refreshed } = (await this.store.request(
        findRecord<TasApplicationModel>('tas-application', application.id!, { reload: true })
      )) as { content: { data: TasApplicationModel } };
      this.approvalQueueRefresh.recordApproval(
        refreshed.data.id,
        refreshed.data.transitionLog.at(-1)?.timestamp
      );

      this.args.onDraftCommentChange('');

      if (this.hasConfirmedEvidenceApprovalWaiver) {
        await this.tuitionAssistance.triggerAutoEvidenceApprovalForApplication(
          application,
          this.comment
        );
      }

      const applicantName =
        application.tasProgramInstance?.employee?.person?.fullName ?? 'Applicant';
      const programName =
        application.tasProgramInstance?.tasProgramTemplate?.programName ?? 'program';
      this.notifications.add(`Application approved for ${applicantName} — ${programName}`, {
        appearance: 'success',
      });

      this.router.transitionTo(this.approvalQueueRoute);
    } catch (error) {
      console.error('Approval submission failed:', error);
      this.submissionError =
        error instanceof Error ? error.message : 'An error occurred during approval';
    } finally {
      this.isProcessing = false;
    }
  });

  <template>
    <LimitsSummary
      @application={{@application}}
      @employee={{@application.tasProgramInstance.employee}}
      @approvedTotalToValidate={{if
        this.isCreditBased
        @application.totalCredits
        this.approvedTotal
      }}
      @approvedCourseCashTotalToValidate={{if
        this.showCourseCashField
        this.cardFinalApprovedTotal
        0
      }}
      @ignoreLimitsValidation={{this.isPostPaymentReview}}
      @shouldFetchLimits={{true}}
      as |Summary|
    >
      <Modal
        @isOpen={{@show}}
        @onClose={{this.handleClose}}
        @nonModal={{@nonModal}}
        @size={{this.modalSize}}
        @isAsideOpen={{this.isInstructionsOpen}}
        @asideSize="sm"
        @asideLabel={{t "tuition_assistance.application_approval.approver_instructions"}}
        data-test-approve-modal
      >
        <:header>
          {{! pr-6 keeps the toggle clear of the modal's own close button }}
          <div class="tio-row-between gap-4 pr-6">
            <div>
              <div>Approve Application</div>
              <div class="tio-text-sm-normal tio-fg-muted mt-0.5">
                {{@application.tasProgramInstance.employee.person.fullName}}
                —
                {{@application.tasProgramInstance.tasProgramTemplate.programName}}
              </div>
            </div>
            {{#if this.hasApproverInstructions}}
              {{! This header toggle is the drawer’s only open/close control. }}
              <TooltipButton
                @tooltip={{if
                  this.isInstructionsOpen
                  (t "common.tas.approval.approve_modal.instructions_hide")
                  (t "common.tas.approval.approve_modal.instructions_show")
                }}
                @tooltipPlacement="top"
                @intent="default"
                @appearance="minimal"
                @size="sm"
                @icon={{PhSidebarSimple}}
                @isActive={{this.isInstructionsOpen}}
                aria-expanded={{if this.isInstructionsOpen "true" "false"}}
                {{on "click" this.toggleInstructions}}
                data-test-approve-modal-instructions-toggle
              />
            {{/if}}
          </div>
        </:header>

        <:body>
          <VStack>
            {{! Empty sandbox disables scripts and same-origin access, isolating admin-authored header HTML. }}
            {{#if this.headerHtml}}
              <iframe
                title="Program information"
                srcdoc={{this.headerHtml}}
                sandbox=""
                class="block h-[60vh] w-full rounded-lg border tio-border-default tio-bg-surface"
                data-test-approval-header-html
              ></iframe>
            {{/if}}

            {{#if this.submissionError}}
              <AlertBanner @intent="warning" data-test-approve-modal-error>
                {{this.submissionError}}
              </AlertBanner>
            {{/if}}

            <Summary.LimitsAlertForApproverIfApplicable class="text-sm flex" />

            {{#unless this.isPostPaymentReview}}
              <div
                class="app-status-panel app-status-panel--brand p-4 space-y-2"
                data-test-remaining-benefit-summary
              >
                <p class="tio-text-xs-medium app-status-text-brand uppercase tracking-wider">
                  Remaining Budget
                </p>
                <div class="space-y-1.5">
                  {{#unless Summary.isUnlimitedRemainingBenefit}}
                    <dl class="grid grid-cols-[1fr_auto] gap-y-2 items-center">
                      <Summary.MaxAnnualBenefitLineItem
                        @labelClass="text-sm tio-fg-secondary"
                        @valueClass="tio-text-sm-semibold tio-fg-primary"
                      />
                      <Summary.BenefitPaidForYearLineItem
                        @labelClass="text-sm tio-fg-secondary"
                        @valueClass="tio-text-sm-semibold tio-fg-primary"
                      />
                    </dl>
                  {{/unless}}
                  <dl
                    class="grid grid-cols-[1fr_auto] gap-y-2 items-center border-t app-divider-brand pt-1.5"
                  >
                    {{#if Summary.isUnlimitedRemainingBenefit}}
                      <dt class="tio-text-sm-medium app-status-text-brand">Remaining Benefit</dt>
                      <dd class="tio-text-base-bold app-status-text-brand text-right">Unlimited</dd>
                    {{else}}
                      <Summary.RemainingBenefitLineItem
                        @labelClass="tio-text-sm-medium app-status-text-brand"
                        @valueClass="tio-text-base-bold app-status-text-brand"
                      />
                    {{/if}}
                  </dl>
                </div>
              </div>
            {{/unless}}

            {{#if this.canSkipFinalApprovalOrAttachmentsWithChecklist}}
              <div class="space-y-3">
                <h3 class="tio-text-sm-medium">Bypass evidence approval step</h3>
                <TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklist
                  @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
                  @didChange={{this.didChangeApprovalWaiverChecklist}}
                />
              </div>
            {{/if}}

            {{#if this.isPostPaymentReview}}
              <div class="rounded-md tio-bg-sunken p-4 space-y-2">
                <div class="tio-row-between">
                  <span class="text-sm tio-fg-muted">{{t
                      "common.tas.approval.post_payment_review.paid_amount"
                    }}</span>
                  <span class="tio-text-sm-semibold tio-fg-primary">{{safeFormatNumber
                      (divide (or @application.amounts.paid 0) 100)
                      style="currency"
                      currency="USD"
                    }}</span>
                </div>
                <p class="text-xs tio-fg-disabled">{{t
                    "common.tas.approval.post_payment_review.payment_processed"
                  }}</p>
              </div>
            {{else}}
              <div class="space-y-4">
                {{#if this.requestedExpenseRows.length}}
                  <div
                    class="rounded-md tio-bg-sunken p-4 space-y-2"
                    data-test-requested-expenses-summary
                  >
                    <p class="tio-text-xs-medium tio-fg-muted uppercase tracking-wider">
                      {{t "common.tas.approval.approve_modal.requested_expenses.heading"}}
                    </p>
                    <dl class="grid grid-cols-[1fr_auto] gap-y-1.5 items-center">
                      {{#each this.requestedExpenseRows as |row|}}
                        <dt class="text-sm tio-fg-secondary">{{row.label}}</dt>
                        <dd class="text-sm tio-fg-primary text-right">{{safeFormatNumber
                            (divide row.amount 100)
                            style="currency"
                            currency="USD"
                          }}</dd>
                      {{/each}}
                      <dt
                        class="tio-text-sm-medium tio-fg-primary border-t tio-border-default pt-1.5"
                      >{{t "common.tas.approval.approve_modal.requested_expenses.total"}}</dt>
                      <dd
                        class="tio-text-sm-semibold tio-fg-primary text-right border-t tio-border-default pt-1.5"
                        data-test-requested-expenses-total
                      >{{safeFormatNumber
                          (divide this.requestedExpensesTotal 100)
                          style="currency"
                          currency="USD"
                        }}</dd>
                    </dl>
                  </div>
                {{/if}}

                {{#if this.isCalculatedApplicationTotal}}
                  {{#if this.showCourseCashField}}
                    <div class="grid grid-cols-2 gap-2 items-center">
                      <label class="font-semibold">
                        {{this.reimbursementPercentage}}% reimbursement of
                      </label>
                      <Input
                        @value={{this.prePercentageCourseCashTotalInputValue}}
                        @onChange={{this.updateCourseCashFromPrePercentage}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        @description="Course Cash - Tuition"
                        data-test-pre-percentage-cc-total
                      />

                      <label class="font-semibold">{{t
                          "common.tas.approval.approve_modal.approved_course_cash_total"
                        }}</label>
                      <Input
                        @value={{String (divide this.cardFinalApprovedTotal 100)}}
                        @isDisabled={{true}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-card-final-approved-total-input
                      />
                    </div>

                    <hr class="my-2" />

                    <div class="grid grid-cols-2 gap-2 items-center">
                      <label class="font-semibold">
                        {{this.reimbursementPercentage}}% reimbursement of
                      </label>
                      <Input
                        @value={{this.prePercentageTotalInputValue}}
                        @onChange={{this.updateApprovedTotalFromPrePercentage}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        @description="All other expenses"
                        data-test-pre-percentage-total
                      />

                      <label class="font-semibold">{{t
                          "common.tas.approval.approve_modal.approved_reimbursement_total"
                        }}</label>
                      <Input
                        @value={{String (divide this.approvedTotal 100)}}
                        @onChange={{this.updateApprovedTotal}}
                        @isDisabled={{true}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-approved-total-input
                      />
                    </div>
                  {{else}}
                    <div class="grid grid-cols-2 gap-2 items-center">
                      <label class="font-semibold">
                        {{this.reimbursementPercentage}}% reimbursement of
                      </label>
                      <Input
                        @value={{this.prePercentageTotalInputValue}}
                        @onChange={{this.updateApprovedTotalFromPrePercentage}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-pre-percentage-total
                      />

                      <label class="font-semibold">Approved Total</label>
                      <Input
                        @value={{String (divide this.approvedTotal 100)}}
                        @onChange={{this.updateApprovedTotal}}
                        @isDisabled={{true}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-approved-total-input
                      />
                    </div>
                  {{/if}}
                {{else}}
                  {{#if this.showCourseCashField}}
                    <Input
                      @label="Course Cash Amount"
                      @value={{this.cardFinalApprovedTotalInputValue}}
                      @onChange={{this.updateCardFinalApprovedTotal}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      @description="Course Cash - Tuition"
                      data-test-card-final-approved-total-input
                    />
                  {{/if}}

                  {{#if this.showCourseCashField}}
                    <Input
                      @label="All Other Expenses"
                      @value={{this.approvedTotalInputValue}}
                      @onChange={{this.updateApprovedTotal}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      data-test-approved-total-input
                    />
                  {{else}}
                    {{#if this.isCreditBased}}
                      <div class="space-y-4">
                        <div class="tio-row-between py-2 border-b tio-border-default">
                          <span class="tio-text-sm-medium tio-fg-secondary">Total Credits</span>
                          <span
                            class="tio-text-sm-semibold tio-fg-primary"
                          >{{@application.totalCredits}} credits</span>
                        </div>
                        <Input
                          @value={{this.approvedTotalInputValue}}
                          @label={{t
                            "common.tas.approval.approve_modal.approved_total_for_reimbursement"
                          }}
                          @onChange={{this.updateApprovedTotal}}
                          {{inputmask
                            alias="currency"
                            prefix="$"
                            unmaskAsNumber=true
                            digits="2"
                            autoUnmask=true
                          }}
                          data-test-approved-total-input
                        />
                      </div>
                    {{else}}
                      <Input
                        @value={{this.approvedTotalInputValue}}
                        @label={{t
                          "common.tas.approval.approve_modal.approved_total_for_reimbursement"
                        }}
                        @onChange={{this.updateApprovedTotal}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-approved-total-input
                      />
                    {{/if}}
                  {{/if}}
                {{/if}}
              </div>
            {{/if}}

            <div class="space-y-2">
              <h3 class="tio-text-sm-medium">Leave a Comment</h3>
              {{#if this.approveTextAreaOptions.length}}
                <SelectTextArea
                  @options={{this.approveTextAreaOptions}}
                  @optionLabel="title"
                  @optionValue="body"
                  @value={{this.comment}}
                  @onChange={{this.updateComment}}
                  @label="Select a response option or write your own"
                  @hideLabel={{true}}
                  @selectPlaceholder="Select a response..."
                  @placeholder="Or write your own comment..."
                  @multiSelect={{true}}
                />
              {{else}}
                <Textarea
                  @value={{this.comment}}
                  @onChange={{this.updateComment}}
                  data-test-approve-comment
                />
              {{/if}}
            </div>

            {{#if this.employeeHasAppsInRepayment}}
              <ErrorMessages
                @showErrors={{true}}
                @error="Please keep in mind that the employee has applications in repayment."
                class="text-sm"
              />
            {{/if}}

            {{#if this.isTasParticipantInactive}}
              <ErrorMessages
                @showErrors={{true}}
                @error="This employee is inactive in the TAS program."
                class="text-sm"
              />
            {{/if}}
          </VStack>

          <ConfirmationDialog
            @isOpen={{this.showConfirmApproverOverridePrompt}}
            @title="Out of Sequence Approver"
            @intent="warning"
            @onConfirm={{this.submitApprovalTask.perform}}
            @onCancel={{this.cancelApproverOverride}}
            @confirmText="Yes"
            @cancelText="No"
          >
            <p class="mb-2 font-semibold">
              You are not the expected approver for this application. Are you sure you want to
              proceed with this approval?
            </p>
          </ConfirmationDialog>
        </:body>
        <:footer>
          {{#if (or this.hasValidationBlockers Summary.isDisabledSubmitForApprover)}}
            <div class="text-xs tio-fg-danger mb-3">
              <p class="font-medium">{{t
                  "common.tas.approval.approve_modal.validation_blockers.header"
                }}</p>
              <ul class="mt-0.5 list-disc pl-4 space-y-0.5">
                {{#each this.validationBlockersForDisplay as |reason|}}
                  <li>{{reason}}</li>
                {{/each}}
                {{#if Summary.isDisabledSubmitForApprover}}
                  <li>{{t
                      "common.tas.approval.approve_modal.validation_blockers.limits_exceeded"
                    }}</li>
                {{/if}}
              </ul>
            </div>
          {{/if}}
          <Footer
            @isRunning={{this.submitApprovalTask.isRunning}}
            @submitText="Approve"
            @cancelText="Cancel"
            @onSubmit={{this.attemptSubmitApproval}}
            @onCancel={{this.handleClose}}
            @submitDisabled={{or (not this.canSubmitApproval) Summary.isDisabledSubmitForApprover}}
          />
        </:footer>

        <:aside>
          {{! pl-20 = the -ml-12 tuck (48px) plus a 32px gutter, so the text sits clear
          of the modal edge rather than hugging it }}
          {{! The header toggle owns closing; this drawer has no second close control. }}
          <div class="flex-none pl-20 pr-4 py-3 border-b tio-border-default">
            <h3 class="tio-text-sm-semibold tio-fg-primary">
              {{t "tuition_assistance.application_approval.approver_instructions"}}
            </h3>
          </div>
          <div
            class="flex-1 min-h-0 overflow-y-auto pl-20 pr-4 py-3"
            data-test-approve-modal-instructions
          >
            <ul class="list-disc ml-4 space-y-3 tio-fg-secondary text-sm leading-relaxed">
              {{#each this.approverInstructions as |item|}}
                <li>
                  {{#if item.heading}}
                    <span class="tio-text-sm-semibold tio-fg-primary block">{{item.heading}}</span>
                  {{/if}}
                  {{htmlSafe item.body}}
                </li>
              {{/each}}
            </ul>
          </div>
        </:aside>
      </Modal>
    </LimitsSummary>
  </template>
}

export default ApproveModal;
